import React, { useState, useEffect, useRef } from "react";
import CustomButton from "../../src/pages/CustomButton";
import {
  fetchStates,
  fetchEmployees,
  loadScheme,
  fetchDivisions,
  fetchEmployeeChemistSchemeReport,
} from "../../src/services/stockistService";
import Loader from "../dist/Loader";

const Scheme = () => {
  const currentDate = new Date();

  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState("");

  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");

  const [schemeTypes, setSchemeTypes] = useState([]);
  const [selectedScheme, setSelectedScheme] = useState("");

  const [divisions, setDivisions] = useState([]);
  const [selectedDivision, setSelectedDivision] = useState("");

  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(
    currentDate.getMonth() + 1,
  );
  const [showTable, setShowTable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [report, setReport] = useState([]);

  const tableRef = useRef(null);

  const years = [2024, 2025, 2026];
  const months = [
    { id: 1, name: "January" },
    { id: 2, name: "February" },
    { id: 3, name: "March" },
    { id: 4, name: "April" },
    { id: 5, name: "May" },
    { id: 6, name: "June" },
    { id: 7, name: "July" },
    { id: 8, name: "August" },
    { id: 9, name: "September" },
    { id: 10, name: "October" },
    { id: 11, name: "November" },
    { id: 12, name: "December" },
  ];
  const monthLabel =
    months.find((m) => m.id === Number(selectedMonth))?.name || "Month";

  // Load States
  useEffect(() => {
    const loadStates = async () => {
      try {
        const list = await fetchStates();
        setStates(list || []);
      } catch (err) {
        console.error("Failed to load states", err);
        setStates([]);
      }
    };
    loadStates();
  }, []);

  // Load Employees when State changes
  useEffect(() => {
    const loadEmployees = async (stateId) => {
      if (!stateId) {
        setEmployees([]);
        return;
      }
      try {
        const list = await fetchEmployees(stateId);
        setEmployees(list || []);
      } catch (err) {
        console.error("Failed to load employees", err);
        setEmployees([]);
      }
    };
    loadEmployees(selectedState);
  }, [selectedState]);

  // Load Schemes when State changes
  useEffect(() => {
    const loadSchemeList = async (stateId) => {
      if (!stateId) {
        setSchemeTypes([]);
        return;
      }
      try {
        const list = await loadScheme(stateId);
        setSchemeTypes(list || []);
      } catch (err) {
        console.error("Failed to load schemes", err);
        setSchemeTypes([]);
      }
    };
    loadSchemeList(selectedState);
  }, [selectedState]);

  // Load Divisions (no state dependency)
  useEffect(() => {
    const loadDivisions = async () => {
      try {
        const list = await fetchDivisions();
        setDivisions(list || []);
      } catch (err) {
        console.error("Failed to load divisions", err);
        setDivisions([]);
      }
    };
    loadDivisions();
  }, []);

  // GO BUTTON CLICK
  const handleGoClick = async () => {
    if (!selectedState || !selectedEmployee) {
      alert("Please select state and employee");
      return;
    }

    setIsLoading(true);
    setShowTable(false);
    try {
      const payload = {
        state_id: parseInt(selectedState, 10),
        year: parseInt(selectedYear, 10),
        month: parseInt(selectedMonth, 10),
        division: divisions.find((d) => d.id === parseInt(selectedDivision, 10))
          ?.name,
        employee_id: parseInt(selectedEmployee, 10),
        scheme_id: selectedScheme ? parseInt(selectedScheme, 10) : undefined,
      };

      const data = await fetchEmployeeChemistSchemeReport(payload);
      const dataArr = Array.isArray(data) ? data : data?.data || [];
      setReport(dataArr);
      setShowTable(true);
    } catch (err) {
      console.error("Failed to fetch report", err);
      alert("Failed to fetch report. Check console for details.");
      setReport([]);
      setShowTable(true);
    } finally {
      setIsLoading(false);
    }
  };

  // PRINT
  const handlePrint = () => {
    if (!tableRef.current) {
      alert("No table to print");
      return;
    }
    const tableHtml = tableRef.current.outerHTML;
    const style = `
      <style>
        body { font-family: Inter, Arial, sans-serif; padding: 12px; }
        table { border-collapse: collapse; width: 100%; }
        th, td { border: 1px solid #1f2937; padding: 6px 8px; font-size: 12px; line-height: 1.2; }
        thead { background: #2563eb; color: white; }
        tbody tr:nth-child(even) { background: #f8fafc; }
        ul { padding-left: 12px; margin: 0; }
        @media print { body { -webkit-print-color-adjust: exact; } }
      </style>
    `;

    const printWindow = window.open("", "_blank", "width=1000,height=700");
    if (!printWindow) {
      alert("Unable to open print window. Check popup settings.");
      return;
    }
    printWindow.document.open();
    printWindow.document.write(
      `<html><head><title>Scheme report</title>${style}</head><body>`,
    );
    printWindow.document.write(tableHtml);
    printWindow.document.write("</body></html>");
    printWindow.document.close();

    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 300);
  };

  // EXPORT CSV
  const handleExportExcel = () => {
    if (!report || !report.length) {
      alert("No data to export");
      return;
    }

    const headers = [
      "S.No",
      "Emp ID",
      "Employee Name",
      "Retailer ID",
      "Retailer Name",
      "Retailer City",
      "Total POB",
      "Scheme Gift",
      "Multiple Gift",
      "Total Gift",
      "Products",
      `${monthLabel} Count`,
    ];

    const escapeCsv = (value) => {
      if (value === null || value === undefined) return "";
      const str = String(value);
      if (/[",\n\r]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const rows = report.map((row, idx) => {
      const empId = row.employee_id ?? row.emp_id ?? "-";
      const employeeName =
        `${row.employee_name ?? row.employee ?? row.first_name ?? ""} ${
          row.last_name ?? ""
        }`.trim() || "-";
      const retailerId = row.chemist_id ?? row.retailer_id ?? "-";
      const retailerName = row.chemist_name ?? row.retailer_name ?? "-";
      const retailerCity = row.city ?? row.retailer_city ?? "-";
      const totalPOB = row.total_pob ?? 0;
      const schemeGift = row.scheme_gift ?? 0;
      const multipleGift = row.multiple_gift ?? 0;
      const totalGift = row.total_gift ?? schemeGift + multipleGift;
      const products =
        Array.isArray(row.products) && row.products.length > 0
          ? row.products
              .map(
                (p) =>
                  `${p.product_name ?? "Unknown"}(${p.product_id ?? "-"}) x ${
                    p.quantity ?? p.qty ?? "-"
                  }`,
              )
              .join(" | ")
          : "";

      const monthCount = row.month_count ?? "-";

      return [
        idx + 1,
        empId,
        employeeName,
        retailerId,
        retailerName,
        retailerCity,
        totalPOB,
        schemeGift,
        multipleGift,
        totalGift,
        products,
        monthCount,
      ];
    });

    const csvArray = [headers.map(escapeCsv).join(",")].concat(
      rows.map((r) => r.map(escapeCsv).join(",")),
    );
    const csvContent = csvArray.join("\r\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

    const now = new Date();
    const filename = `chemistscheme_${now.getFullYear()}${(now.getMonth() + 1)
      .toString()
      .padStart(2, "0")}${now.getDate().toString().padStart(2, "0")}.csv`;

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 border-2 border-gray-300 rounded-lg bg-gray-50">
      {/* overlay loader */}
      {isLoading && <Loader onCancel={() => setIsLoading(false)} />}
      <div className="p-4 border-2 border-gray-300 rounded-lg bg-gray-50">
        <div>
          <h2 className="text-2xl font-bold flex justify-center  gap-2">
            <span className="text-blue-700">RTR</span>
            <span className="text-blue-500">Scheme</span>
          </h2>
        </div>

        {/* ROW 1 */}
        <div className="grid grid-cols-2 gap-30 items-center justify-items-center">
          {/* State */}
          <div className="flex items-center gap-4">
            <label className="font-medium whitespace-nowrap">State</label>
            <select
              className="border border-gray-300 rounded-md w-56 px-3 py-1"
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
            >
              <option value="">Select State</option>
              {states.map((state) => (
                <option key={state.id} value={state.id}>
                  {state.name}
                </option>
              ))}
            </select>
          </div>

          {/* Employee */}
          <div className="flex items-center gap-4">
            <label className="font-medium whitespace-nowrap">Employee</label>
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              className="border border-gray-300 rounded-md w-56 px-3 py-1"
            >
              <option value="">Select Employee</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {`${emp.first_name} ${emp.last_name} || ${emp.designation} || ${emp.city_name}`.trim()}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* ROW 2 */}
        <div className="grid grid-cols-2 gap-30 items-center justify-items-center">
          {/* Scheme Type */}
          <div className="flex items-center gap-2 mr-12">
            <label className="font-medium whitespace-nowrap">Scheme Type</label>
            <select
              value={selectedScheme}
              onChange={(e) => setSelectedScheme(e.target.value)}
              className="border border-gray-300 rounded-md w-56 px-3 py-1"
            >
              <option value="">Select Scheme</option>
              {schemeTypes.map((scheme) => (
                <option key={scheme.id} value={scheme.id}>
                  {scheme.scheme_name}
                </option>
              ))}
            </select>
          </div>

          {/* Division */}
          <div className="flex items-center gap-4 mt-5 ml-5">
            <label className="font-medium whitespace-nowrap">Division</label>
            <select
              value={selectedDivision}
              onChange={(e) => setSelectedDivision(e.target.value)}
              className="border border-gray-300 rounded-md w-56 px-3 py-1"
            >
              <option value="">Select Division</option>
              {divisions.map((div) => (
                <option key={div.id} value={div.id}>
                  {div.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* ROW 3 */}
        <div className="grid grid-cols-2 gap-30 items-center justify-items-center">
          {/* Year */}
          <div className="flex items-center gap-4">
            <label className="font-medium whitespace-nowrap">Year</label>

            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="border border-gray-300 rounded-md w-56 px-3 py-1"
            >
              <option value="">Years</option>
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          {/* Month */}
          <div className="flex items-center gap-4 mt-5 ml-6">
            <label className="font-medium whitespace-nowrap">Month</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="border border-gray-300 rounded-md w-56 px-3 py-1"
            >
              <option value="">Month</option>
              {months.map((month) => (
                <option key={month.id} value={month.id}>
                  {month.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* SUBMIT BUTTON */}
        <div className="flex justify-center mt-4">
          <CustomButton
            variant="go"
            onClick={handleGoClick}
            disabled={isLoading}
          >
            {isLoading ? "Loading..." : "GO"}
          </CustomButton>
        </div>
      </div>

      {/* Table */}
      {showTable && (
        <div className="p-2  border-2 border-gray-300 rounded-lg bg-white mt-5">
          <div className="border rounded shadow  table-wrapper">
            <table className="unlock-dcr-leave-table text-sm " ref={tableRef}>
              <thead className="bg-blue-500 text-white">
                <tr className="bg-blue-500 text-white">
                  <th className="px-3 py-2 border">S.No</th>
                  <th className="px-3 py-2 border">Emp ID</th>
                  <th className="px-3 py-2 border">Employee Name</th>
                  <th className="px-3 py-2 border">Retailer ID</th>
                  <th className="px-3 py-2 border">Retailer Name</th>
                  <th className="px-3 py-2 border">Retailer City</th>
                  <th className="px-3 py-2 border">Total POB</th>
                  <th className="px-3 py-2 border">Scheme Gift</th>
                  <th className="px-3 py-2 border">Multiple Gift</th>
                  <th className="px-3 py-2 border">Total Gift</th>
                  <th className="px-3 py-2 border">Products</th>
                  <th className="px-3 py-2 border">{monthLabel} Count</th>
                </tr>
              </thead>
              <tbody>
                {report.length > 0 ? (
                  report.map((row, idx) => {
                    const empId = row.employee_id ?? row.emp_id ?? "-";
                    const employeeName =
                      `${
                        row.employee_name ??
                        row.employee ??
                        row.first_name ??
                        ""
                      } ${row.last_name ?? ""}`.trim() || "-";
                    const retailerId = row.chemist_id ?? row.retailer_id ?? "-";
                    const retailerName =
                      row.chemist_name ?? row.retailer_name ?? "-";
                    const retailerCity = row.city ?? row.retailer_city ?? "-";
                    const totalPOB = row.total_pob ?? 0;
                    const schemeGift = row.scheme_gift ?? 0;
                    const multipleGift = row.multiple_gift ?? 0;
                    const totalGift =
                      row.total_gift ?? schemeGift + multipleGift;
                    const products = row.products ?? [];

                    return (
                      <tr key={idx} className="odd:bg-white even:bg-gray-50">
                        <td className="px-3 py-2 border">{idx + 1}</td>
                        <td className="px-3 py-2 border">{empId}</td>
                        <td className="px-3 py-2 border">{employeeName}</td>
                        <td className="px-3 py-2 border">{retailerId}</td>
                        <td className="px-3 py-2 border">{retailerName}</td>
                        <td className="px-3 py-2 border">{retailerCity}</td>
                        <td className="px-3 py-2 border">{totalPOB}</td>
                        <td className="px-3 py-2 border text-center">
                          {schemeGift}
                        </td>
                        <td className="px-3 py-2 border text-center">
                          {multipleGift}
                        </td>
                        <td className="px-3 py-2 border text-center">
                          {totalGift}
                        </td>
                        <td className="px-3 py-2 border">
                          {products.length > 0 ? (
                            <ul className="list-none m-0 p-0 text-xs">
                              {products.map((p, i) => (
                                <li key={i} className="py-0.5">
                                  <strong>{p.product_name ?? "Unknown"}</strong>{" "}
                                  ({p.product_id ?? "-"}) x{" "}
                                  {p.quantity ?? p.qty ?? "-"}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <span className="text-gray-500">No products</span>
                          )}
                        </td>
                        <td className="px-3 py-2 border text-center">
                          {row.month_count ?? "-"}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={12}
                      className="text-center text-gray-500 py-6 border"
                    >
                      No data found for selected filters
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex gap-4 justify-center">
            <CustomButton onClick={handlePrint} variant="print">
              🖨 Print
            </CustomButton>
            <CustomButton onClick={handleExportExcel} variant="export">
              📥 Export
            </CustomButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default Scheme;
